import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { API, setAccessToken } from '../lib/api';
import { identifyUser, resetUser, trackSignUp, trackLogin } from '../lib/analytics';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login');

  // Fetch user profile from backend, auto-creating for OAuth users if needed
  const fetchProfile = useCallback(async (accessToken) => {
    // Update the cached token for API calls
    if (accessToken) {
      setAccessToken(accessToken);
    }
    try {
      const response = await API.get('/auth/me');
      setUser(response.data);
      setIsAuthenticated(true);
      identifyUser(response.data);
      return { isNew: false };
    } catch (error) {
      // If 401 (no profile row), auto-create one (handles first Google OAuth login)
      if (error.response && error.response.status === 401) {
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (currentSession) {
            const name =
              currentSession.user?.user_metadata?.full_name ||
              currentSession.user?.user_metadata?.name ||
              currentSession.user?.email ||
              '';
            const registerResp = await API.post('/auth/register-profile', { name });
            setUser(registerResp.data);
            setIsAuthenticated(true);
            identifyUser(registerResp.data);
            return { isNew: true };
          }
        } catch (registerError) {
          console.error('Failed to auto-create profile:', registerError);
        }
      }
      console.error('Failed to fetch profile:', error);
      setUser(null);
      setIsAuthenticated(false);
      return { isNew: false };
    }
  }, []);

  useEffect(() => {
    let initialSessionHandled = false;

    // onAuthStateChange handles login, logout, token refresh, and OAuth callbacks
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('[Auth]', event, newSession ? 'has session' : 'no session');

        setSession(newSession);

        if (newSession) {
          setAccessToken(newSession.access_token);

          // Only fetch profile on INITIAL_SESSION or SIGNED_IN (not TOKEN_REFRESHED, etc.)
          if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
            const result = await fetchProfile(newSession.access_token);
            // OAuth signup/login conversions are tracked here (email flows are
            // tracked explicitly in AuthModal to avoid double-counting). Only on
            // an explicit SIGNED_IN, never on INITIAL_SESSION (page reload).
            if (event === 'SIGNED_IN') {
              const provider = newSession.user?.app_metadata?.provider || 'email';
              if (provider !== 'email') {
                if (result?.isNew) trackSignUp(provider);
                else trackLogin(provider);
              }
            }
          }

          // Clean OAuth hash fragment from URL
          if (window.location.hash.includes('access_token')) {
            window.history.replaceState(null, '', window.location.pathname);
          }
        } else {
          setAccessToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }

        if (event === 'INITIAL_SESSION') {
          initialSessionHandled = true;
          setLoading(false);
        }
      }
    );

    // Fallback: if INITIAL_SESSION never fires (navigator lock issue),
    // getSession ensures we still resolve loading state
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (initialSessionHandled) return; // onAuthStateChange already handled it
      console.log('[Auth] getSession fallback:', initialSession ? 'has session' : 'no session');
      if (initialSession) {
        setSession(initialSession);
        setAccessToken(initialSession.access_token);
        fetchProfile(initialSession.access_token).then(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }).catch(() => {
      if (!initialSessionHandled) setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const login = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    resetUser();
    setAccessToken(null);
    setUser(null);
    setSession(null);
    setIsAuthenticated(false);
  };

  const openAuthModal = useCallback((tab = 'login') => {
    setAuthModalTab(tab);
    setShowAuthModal(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setShowAuthModal(false);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, session, loading, isAuthenticated, login, logout, fetchProfile,
      showAuthModal, authModalTab, openAuthModal, closeAuthModal
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
